import { useCallback } from 'react';
import { toast } from 'sonner';
import type { UseMutationResult } from '@tanstack/react-query';
import type { z } from 'zod';

import { logger } from '@/shared/lib';
import { extractApiErrorMessage } from '@/shared/lib/errors/extractApiErrorMessage';
import { commitOrPushErrors, useZodForm } from '@/shared/lib/forms';

export interface UseDetailActionOptions<TDraft, TVariables, TData> {
  readonly schema: z.ZodType<TDraft>;
  readonly defaultValues: TDraft;
  readonly mutation: UseMutationResult<TData, unknown, TVariables, unknown>;
  /** Map a validated draft into the mutation's variables. */
  readonly buildVariables: (draft: TDraft) => TVariables;
  readonly successMessage: string;
  readonly errorTitle: string;
  /** Verb phrase used in ``logger.error`` (e.g. "approve refill" → "Failed to approve refill"). */
  readonly logKey: string;
  readonly logContext?: Record<string, unknown>;
  readonly onCompleted?: () => void;
}

/**
 * Bundles the detail-sheet action pattern: ``useZodForm`` + Zod gate +
 * ``mutateAsync`` + toast + reset + optional ``onCompleted``. Each form sheet
 * action (refill approve/deny, rxChange approve/deny/reconcile/change-patient)
 * collapses to a draft schema, a ``buildVariables`` mapper, and a copy block.
 */
export function useDetailAction<TDraft, TVariables, TData>(
  options: UseDetailActionOptions<TDraft, TVariables, TData>
) {
  const {
    schema,
    defaultValues,
    mutation,
    buildVariables,
    successMessage,
    errorTitle,
    logKey,
    logContext,
    onCompleted,
  } = options;
  const { form, validateDraft } = useZodForm<TDraft>({ schema, defaultValues });

  const handleSubmit = useCallback(async () => {
    const draft = commitOrPushErrors(form, validateDraft());
    if (draft === null) return;
    try {
      await mutation.mutateAsync(buildVariables(draft));
      toast.success(successMessage);
      form.reset(defaultValues);
      onCompleted?.();
    } catch (error: unknown) {
      logger.error(`Failed to ${logKey}`, { ...logContext, error });
      toast.error(errorTitle, {
        description: extractApiErrorMessage(error, 'Please try again.'),
      });
    }
  }, [
    buildVariables,
    defaultValues,
    errorTitle,
    form,
    logContext,
    logKey,
    mutation,
    onCompleted,
    successMessage,
    validateDraft,
  ]);

  return { form, handleSubmit };
}
